
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Send, AlertCircle, CheckCircle, XCircle } from "lucide-react";

const SubscriptionWebhookTester = () => {
  const { toast } = useToast();
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<any[]>([]);

  const testSubscriptionWebhook = async () => {
    setTesting(true);
    setTestResults([]);
    
    try {
      console.log('Testing subscription webhook configuration...');
      
      // Test webhook endpoint availability
      const testPayload = {
        type: 'customer.subscription.updated',
        data: {
          object: {
            id: 'sub_test123',
            status: 'active',
            customer: 'cus_test123',
            current_period_start: Math.floor(Date.now() / 1000),
            current_period_end: Math.floor(Date.now() / 1000) + 2592000, // 30 days
            metadata: {
              membershipId: 'test-membership-id'
            }
          }
        }
      };

      // First, test if webhook function exists and is accessible
      const { data: webhookResponse, error: webhookError } = await supabase.functions.invoke(
        'stripe-subscription-webhook',
        {
          body: testPayload,
          headers: {
            'stripe-signature': 'test-signature',
            'content-type': 'application/json'
          }
        }
      );

      const results = [
        {
          test: 'Webhook Function Accessibility',
          status: webhookError ? 'error' : 'success',
          message: webhookError 
            ? `Webhook function error: ${webhookError.message}` 
            : 'Webhook function is accessible',
          details: webhookResponse
        },
        {
          test: 'Subscription Status Hook',
          status: 'info',
          message: 'Testing subscription status checking...',
          details: 'The useSubscriptionStatus hook will automatically refresh subscription data'
        },
        {
          test: 'Database Integration',
          status: 'info',
          message: 'Webhook will update user_memberships table when subscriptions change',
          details: 'RLS policies ensure users can only see their own subscription data'
        }
      ];

      setTestResults(results);

      toast({
        title: "Webhook Test Completed",
        description: "Check the results below for detailed information",
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Subscription webhook test failed:', error);
      
      setTestResults([{
        test: 'Webhook Test',
        status: 'error',
        message: `Test failed: ${errorMessage}`,
        details: error
      }]);
      
      toast({
        title: "Test Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-blue-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Webhook Tester</h2>
        <p className="text-gray-600 mb-4">
          Test your Stripe subscription webhook configuration to ensure subscription cancellations 
          and updates are properly handled.
        </p>
        
        <Button
          onClick={testSubscriptionWebhook}
          disabled={testing}
          size="lg"
          className="mb-4"
        >
          {testing ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Test Subscription Webhook
        </Button>
      </div>

      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Test Results</h3>
          {testResults.map((result, index) => (
            <Card key={index} className={`border-l-4 ${getStatusColor(result.status)}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center space-x-2">
                    {getStatusIcon(result.status)}
                    <span>{result.test}</span>
                  </CardTitle>
                  <Badge variant={result.status === 'success' ? 'default' : 'secondary'}>
                    {result.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 mb-2">{result.message}</p>
                {result.details && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                      View Details
                    </summary>
                    <pre className="mt-2 p-2 bg-gray-50 rounded overflow-auto">
                      {JSON.stringify(result.details, null, 2)}
                    </pre>
                  </details>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <strong>Next Steps:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>Configure your Stripe webhook endpoint to point to your subscription webhook function</li>
                <li>Add the webhook signing secret to your Supabase Edge Function secrets</li>
                <li>Test with real Stripe events by creating and cancelling test subscriptions</li>
                <li>Monitor the Edge Function logs for any errors during webhook processing</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <strong>Webhook Configuration:</strong>
              <div className="mt-2 space-y-2">
                <p><strong>Webhook URL:</strong> https://doyqmjwlizoqfuabvluf.supabase.co/functions/v1/stripe-subscription-webhook</p>
                <p><strong>Events to listen for:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>customer.subscription.updated</li>
                  <li>customer.subscription.deleted</li>
                  <li>invoice.payment_failed</li>
                  <li>invoice.payment_succeeded</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionWebhookTester;
